/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Card from "components/card";
import {
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { FaAngleLeft } from "react-icons/fa";
import { ImSpinner8 } from "react-icons/im";
import SweetAlert2 from "react-sweetalert2";
import { Auth } from "aws-amplify";

import AccountDetailTab from "./components/AccountDetailTab";
import AccountFeeGroupTab from "./components/AccountFeeGroupTab";
import AccountBalancesTab from "./components/AccountBalancesTab";
import AccountLedgerAdjustmentTab from "./components/AccountLedgerAdjustmentTab";
import AccountWhitelistAddressesTab from "./components/AccountWhitelistAddressesTab";
import AccountSettingsTab from "./components/AccountSettingsTab";
import AccountUsersTab from "./components/AccountUsersTab";
import AccountPayoutMethodsTab from "./components/AccountPayoutMethodsTab";
import AccessControlTab from "../shared/AccessControlTab";
import { useParams, useNavigate } from "react-router-dom";

const AccountDetail = ({ userPermissions }: { userPermissions: any }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFetchingBalance, setIsFetchingBalance] = useState(false);
  const [accountDetailForEdit, setAccountDetailForEdit] = useState(null);
  const [balancesData, setBalancesData] = useState(null);
  const [swalProps, setSwalProps] = useState<any>({});
  const [currentUsername, setCurrentUsername] = useState("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [dialcodeList, setDialcodeList] = useState<any>(null);
  const [dialcodeAutoCompleteOptions, setDialcodeAutoCompleteOptions] =
    useState<any>(null);
  const navigate = useNavigate();
  const { subaccount_id } = useParams();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  useEffect(() => {
    const fetchData = async () => {
      await fetchAccountDetail(subaccount_id);
      await fetchDialcodes();
      const userInfo = await Auth.currentAuthenticatedUser();
      setCurrentUsername(userInfo.username);
    };

    fetchData();
  }, []);

  const fetchDialcodes = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/datasource/countries`
      );

      let countries = response?.data;
      let dialcodeListObj: any = [];
      if (countries.length > 0) {
        countries.forEach((c: any) => {
          if (c.dialcode !== null) {
            if (c.dialcode in dialcodeListObj) {
              dialcodeListObj[c.dialcode] =
                dialcodeListObj[c.dialcode] + ", " + c.country_name;
            } else {
              dialcodeListObj[c.dialcode] =
                "(+" + c.dialcode + ") " + c.country_name;
            }
          }
        });
      }

      const sortedDialcodeList: any = [];
      Object.keys(dialcodeListObj)
        .sort()
        .forEach((key: any) => {
          sortedDialcodeList[key] = dialcodeListObj[key];
        });

      setDialcodeList(sortedDialcodeList);

      let rebuildListArray: any = [];
      Object.entries(sortedDialcodeList).forEach(([key, value]: [any, any]) => {
        let newObj = {
          value: key,
          label: value,
        };
        rebuildListArray.push(newObj);
      });

      setDialcodeAutoCompleteOptions(rebuildListArray);
    } catch (error) {
      console.error("Error fetching dialcodes", error);
    }
  };

  const subaccountsListPath = userRole === "merchant"
    ? `/${userRole}/subaccounts`
    : `/${userRole}/merchants/subaccounts`;

  const fetchAccountDetail = async (
    accountId: string = subaccount_id,
    onlyBalance: boolean = false
  ) => {
    try {
      if (onlyBalance) {
        setIsFetchingBalance(true);
      } else {
        setIsLoading(true);
      }

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/subaccount/get-subaccount-by-id?subaccountid=${accountId}&with_balance=true`
      );

      let accountData = response.data.subaccount?.payload;
      let balancesData = response.data.balances;

      // Check if account data exists, if not redirect to subaccount listing
      if (!accountData) {
        console.error("Subaccount not found");
        navigate(subaccountsListPath);
        return;
      }

      if (!onlyBalance) {
        setAccountDetailForEdit(accountData);
      }
      setBalancesData(balancesData);
      setIsLoading(false);
      setIsFetchingBalance(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching data", error);
      // Redirect to subaccount listing page on error
      navigate(subaccountsListPath);
    }
  };

  const handleClickBack = () => {
    navigate(subaccountsListPath);
  };

  // Check if account has API key data
  const hasApiKeyData =
    accountDetailForEdit?.is_active !== undefined &&
    accountDetailForEdit?.auth1 !== undefined &&
    accountDetailForEdit?.auth2 !== undefined;

  const isMerchant = userRole === "merchant";

  // Define tab configuration with permission checks
  const tabConfig = useMemo(
    () => [
      { label: "Details", show: true },
      { label: "Balances", show: true },
      { label: "Ledger Adjustment", show: !isMerchant },
      { label: "Payout Assets", show: !isMerchant },
      { label: "Fee Group", show: !isMerchant },
      {
        label: "Whitelist Addresses",
        show: userPermissions?.viewWhitelistAddress,
      },
      { label: "API Key", show: hasApiKeyData },
      { label: "Users", show: !isMerchant },
      { label: "Settings", show: true },
    ],
    [userPermissions?.viewWhitelistAddress, hasApiKeyData, isMerchant]
  );

  // Filter visible tabs
  const visibleTabs = useMemo(() => {
    return tabConfig.filter((tab) => tab.show);
  }, [tabConfig]);

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex min-h-[200px] items-center justify-center p-7">
      <ImSpinner8
        className="spinner mt-1 text-primary-500"
        style={{ fontSize: "1.8rem" }}
      />
    </div>
  );

  // Render tab content based on label
  const renderTabContent = (label: string) => {
    // Show loading spinner for tabs that depend on accountDetailForEdit
    if (isLoading && label !== "Balances") {
      return <LoadingSpinner />;
    }

    switch (label) {
      case "Details":
        return (
          <AccountDetailTab
            detail={accountDetailForEdit}
            setSwalProps={setSwalProps}
            dialcodeList={dialcodeList}
            dialcodeAutoCompleteOptions={dialcodeAutoCompleteOptions}
          />
        );
      case "Fee Group":
        return (
          <AccountFeeGroupTab
            detail={accountDetailForEdit}
            setSwalProps={setSwalProps}
            fetchAccountDetail={fetchAccountDetail}
          />
        );
      case "Balances":
        return (
          <AccountBalancesTab
            data={balancesData}
            isFetchingBalance={isFetchingBalance}
          />
        );
      case "Ledger Adjustment":
        return (
          <AccountLedgerAdjustmentTab
            detail={accountDetailForEdit}
            setIsProcessing={setIsProcessing}
            isProcessing={isProcessing}
            setSwalProps={setSwalProps}
            fetchAccountDetail={fetchAccountDetail}
          />
        );
      case "Whitelist Addresses":
        return <AccountWhitelistAddressesTab />;
      case "Payout Assets":
        return (
          <AccountPayoutMethodsTab
            detail={accountDetailForEdit}
            setSwalProps={setSwalProps}
            fetchAccountDetail={fetchAccountDetail}
          />
        );
      case "API Key":
        return (
          <AccessControlTab
            data={accountDetailForEdit}
            mode="subaccount"
            is_active={accountDetailForEdit?.is_active}
            auth1={accountDetailForEdit?.auth1}
            auth2={accountDetailForEdit?.auth2}
            isProcessing={isProcessing}
            setSwalProps={setSwalProps}
            fetchDetail={fetchAccountDetail}
            isOtherTabUpdating={isUpdating}
            setIsUpdating={setIsUpdating}
          />
        );
      case "Users":
        return (
          <AccountUsersTab
            currentUsername={currentUsername}
            subaccountId={subaccount_id}
          />
        );
      case "Settings":
        return (
          <AccountSettingsTab
            detail={accountDetailForEdit}
            setIsProcessing={setIsProcessing}
            isProcessing={isProcessing}
            setSwalProps={setSwalProps}
            fetchAccountDetail={fetchAccountDetail}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Card className="card card-pad !rounded-xl">
        {!isLoading && accountDetailForEdit && accountDetailForEdit !== null ? (
          <>
            <div className="mb-3 flex items-center">
              <div>
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={() => handleClickBack()}
                  className="
                mb-1 inline-flex items-center gap-1
                text-sm font-medium text-gray-700
                transition hover:text-gray-700
                disabled:cursor-not-allowed disabled:opacity-40
                dark:text-gray-400 dark:hover:text-white
              "
                >
                  <FaAngleLeft className="text-base" />
                  <span>Subaccounts</span>
                </button>

                <h3 className="text-xl font-semibold text-navy-700">
                  {accountDetailForEdit.suba_name}
                </h3>
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              className="mb-3 mt-3 h-5 w-40 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
            <div
              className="mb-3 mt-3 h-5 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
          </>
        )}

        <Tabs className="mt-3">
          <TabList className="rounded-tl-custom rounded-tr-custom scrollbar-hide flex flex-wrap overflow-x-auto sm:flex-nowrap">
            {visibleTabs.map((tab, index) => {
              const isDisabled =
                isLoading || isProcessing || accountDetailForEdit === null;
              return (
                <Tab
                  key={index}
                  className={`child-tab rounded-tl-custom rounded-tr-custom sm:text-md whitespace-nowrap bg-white px-4 py-3 pt-4 text-sm text-navy-700 sm:px-6 sm:py-4 sm:pt-6 ${
                    isDisabled ? "pointer-events-none opacity-50" : ""
                  }`}
                >
                  {tab.label}
                </Tab>
              );
            })}
          </TabList>

          <TabPanels className="rounded-bl-custom rounded-br-custom border-t border-gray-200 bg-white">
            {visibleTabs.map((tab, index) => (
              <TabPanel key={index}>{renderTabContent(tab.label)}</TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Card>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default AccountDetail;